import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Link, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { IDuplicateCandidateContact } from 'app/models/Candidate/CandidateProfile';

const useStyles = makeStyles()(() => ({
  root: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
  },
  tableContainer: {
    maxHeight: '32vh',
    width: '100%',
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'auto',
  },
  table: {
    minWidth: 'max-content',
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    color: '#c62828',
    fontSize: '13px',
    fontWeight: 500,
  },
  errorIcon: {
    color: '#c62828',
    fontSize: '18px',
    marginRight: '6px',
  },
  headerCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    backgroundColor: '#f4f4f4',
    fontSize: '12px',
    padding: '4px 8px',
  },
  cell: {
    whiteSpace: 'nowrap',
    fontSize: '12px',
    padding: '4px 8px',
  },
  link: {
    color: '#006FB9',
    cursor: 'pointer',
  },
}));

interface IDuplicateContactTableProps {
  duplicates: IDuplicateCandidateContact[];
}

export const DuplicateContactTable = ({ duplicates }: IDuplicateContactTableProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const getCandidateUrl = (row: IDuplicateCandidateContact) => `/candidate/${row.candidateId}/${row.brandId}`;

  const columns = [
    t('candidate.contactTab.duplicate.candidateId', 'Candidate ID'),
    t('candidate.contactTab.duplicate.candidateName', 'Candidate Name'),
    t('candidate.contactTab.duplicate.cellPhone', 'Cell Phone'),
    t('candidate.contactTab.duplicate.landline', 'Landline'),
    t('candidate.contactTab.duplicate.primaryEmail', 'Primary Email'),
    t('candidate.contactTab.duplicate.secondaryEmail', 'Secondary Email'),
    t('candidate.contactTab.duplicate.brand', 'Brand'),
    t('candidate.contactTab.duplicate.recruiter', 'Recruiter'),
    t('candidate.contactTab.duplicate.perDiemRecruiter', 'Per Diem Recruiter'),
  ];

  return (
    <div data-testid="duplicate-contact-table" className={classes.root}>
      <div className={classes.message}>
        <ErrorOutlineIcon className={classes.errorIcon} />
        {t(
          'candidate.contactTab.duplicate.message',
          'The information entered is already assigned to the following candidate(s). Changes were not saved.',
        )}
      </div>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader size="small" aria-label="duplicate-candidates" className={classes.table}>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col} className={classes.headerCell}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {duplicates.map(row => (
              <TableRow key={`${row.candidateId}-${row.brandId}`} hover>
                <TableCell className={classes.cell}>
                  <Link
                    className={classes.link}
                    href={getCandidateUrl(row)}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {row.candidateId}
                  </Link>
                </TableCell>
                <TableCell className={classes.cell}>
                  <Link
                    className={classes.link}
                    href={getCandidateUrl(row)}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {row.candidateName}
                  </Link>
                </TableCell>
                <TableCell className={classes.cell}>{row.cellPhone || '--'}</TableCell>
                <TableCell className={classes.cell}>{row.landline || '--'}</TableCell>
                <TableCell className={classes.cell}>{row.primaryEmail || '--'}</TableCell>
                <TableCell className={classes.cell}>{row.secondaryEmail || '--'}</TableCell>
                <TableCell className={classes.cell}>{row.brand || '--'}</TableCell>
                <TableCell className={classes.cell}>{row.recruiter || '--'}</TableCell>
                <TableCell className={classes.cell}>{row.perDiemRecruiter || '--'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
